import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  Database,
  Package,
  Briefcase,
  Boxes,
  Tag,
  Truck,
  FolderTree,
  Ruler,
  ShoppingBag,
  Utensils,
  MapPin,
  X,
  QrCode,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import SearchableSelect from "../components/SearchableSelect";
import { masterService } from "../api/masterService";
import * as inventoryService from "../api/inventoryService";
import barcodeLabelService from "../api/barcodeLabelService";
import DataProductTable from "../components/DataProductTable";
import DataAddonTable from "../components/DataAddonTable";
import DataServiceTable from "../components/DataServiceTable";
import DataUnitTable from "../components/DataUnitTable";
import DataBrandTable from "../components/DataBrandTable";
import DataSupplierTable from "../components/DataSupplierTable";
import DataCategoryTable from "../components/DataCategoryTable";
import DataSatuanTable from "../components/DataSatuanTable";
import DataSalesTypeTable from "../components/DataSalesTypeTable";
import DataTableTable from "../components/master/DataTableTable";
import DataStockLocationTable from "../components/master/DataStockLocationTable";
import DataBarcodeLabelTable from "../components/master/DataBarcodeLabelTable";
import AddProductModal from "../components/AddProductModal";
import AddServiceModal from "../components/AddServiceModal";
import AddUnitModal from "../components/AddUnitModal";
import AddBrandModal from "../components/AddBrandModal";
import AddSupplierModal from "../components/AddSupplierModal";
import AddCategoryModal from "../components/AddCategoryModal";
import AddSatuanModal from "../components/AddSatuanModal";
import AddSalesTypeModal from "../components/AddSalesTypeModal";
import AddTableModal from "../components/AddTableModal";
import AddStockLocationModal from "../components/master/AddStockLocationModal";
import CreateBarcodeLabelModal, { BarcodeLabelQuickPrint } from "../components/master/CreateBarcodeLabelModal";
import TableDetailModal from "../components/master/TableDetailModal";
import StockLocationDetailModal from "../components/master/StockLocationDetailModal";
import ConfirmationModal from "../components/ConfirmationModal";
import { useNotification } from "../contexts/NotificationContext";
import { formatDateTime } from "../utils/salesUtils";

const MASTER_DATA_STORAGE_KEY = "masterData_tab_state";
const memoryTabCache = {};

const getSavedTabState = (tabName) => {
  try {
    const raw = sessionStorage.getItem(MASTER_DATA_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed[tabName] || null;
  } catch {
    return null;
  }
};

const saveTabState = (tabName, patch) => {
  try {
    const raw = sessionStorage.getItem(MASTER_DATA_STORAGE_KEY);
    const existing = raw ? JSON.parse(raw) : {};
    existing[tabName] = { ...(existing[tabName] || {}), ...patch };
    sessionStorage.setItem(MASTER_DATA_STORAGE_KEY, JSON.stringify(existing));
  } catch {}
};

export default function MasterData() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const { showSuccess, showError } = useNotification();

  // Map URL path to tab name
  const getTabFromPath = (pathname) => {
    const pathMap = {
      "/master-data/produk": "Data Produk",
      "/master-data/label-barcode": "Label Barcode",
      "/master-data/addon": "Kelola Addon",
      "/master-data/layanan": "Data Layanan",
      "/master-data/unit": "Data Unit",
      "/master-data/merek": "Data Merek",
      "/master-data/supplier": "Data Supplier",
      "/master-data/kategori": "Data Kategori",
      "/master-data/satuan": "Data Satuan",
      "/master-data/tipe-penjualan": "Tipe Penjualan",
      "/master-data/lokasi-stok": "Data Lokasi Stok",
      "/master-data/meja": "Data Meja",
    };
    return pathMap[pathname] || "Data Produk";
  };

  // Get URL slug from tab name
  const getPathFromTab = (tabName) => {
    const tabMap = {
      "Data Produk": "/master-data/produk",
      "Label Barcode": "/master-data/label-barcode",
      "Kelola Addon": "/master-data/addon",
      "Data Layanan": "/master-data/layanan",
      "Data Unit": "/master-data/unit",
      "Data Merek": "/master-data/merek",
      "Data Supplier": "/master-data/supplier",
      "Data Kategori": "/master-data/kategori",
      "Data Satuan": "/master-data/satuan",
      "Tipe Penjualan": "/master-data/tipe-penjualan",
      "Data Lokasi Stok": "/master-data/lokasi-stok",
      "Data Meja": "/master-data/meja",
    };
    return tabMap[tabName] || "/master-data/produk";
  };

  const initialTab = getTabFromPath(location.pathname);
  const initialSaved = getSavedTabState(initialTab) || {};

  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchTerm, setSearchTerm] = useState(initialSaved.searchTerm || "");

  // Modal State
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
  const [isAddUnitModalOpen, setIsAddUnitModalOpen] = useState(false);
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState(false);
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [isAddSatuanModalOpen, setIsAddSatuanModalOpen] = useState(false);
  const [isAddSalesTypeModalOpen, setIsAddSalesTypeModalOpen] = useState(false);
  const [isAddTableModalOpen, setIsAddTableModalOpen] = useState(false);
  const [isAddStockLocationModalOpen, setIsAddStockLocationModalOpen] = useState(false);
  const [isStockLocationDetailModalOpen, setIsStockLocationDetailModalOpen] = useState(false);
  const [isCreateBarcodeModalOpen, setIsCreateBarcodeModalOpen] = useState(false);
  const [selectedBarcodeLabel, setSelectedBarcodeLabel] = useState(null);
  const [quickPrintBarcodeLabel, setQuickPrintBarcodeLabel] = useState(null);
  const [barcodeLabelData, setBarcodeLabelData] = useState(() => memoryTabCache["Label Barcode"]?.data || []);
  const [selectedStockLocation, setSelectedStockLocation] = useState(null);
  const [selectedStockLocationForDetail, setSelectedStockLocationForDetail] = useState(null);

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: () => { },
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSatuan, setSelectedSatuan] = useState(null);
  const [selectedSalesType, setSelectedSalesType] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedTableForDetail, setSelectedTableForDetail] = useState(null);
  const [isTableDetailModalOpen, setIsTableDetailModalOpen] = useState(false);
  const [tableData, setTableData] = useState(() => memoryTabCache["Data Meja"]?.data || []);
  const [stockLocationData, setStockLocationData] = useState(() => memoryTabCache["Data Lokasi Stok"]?.data || []);

  // Table State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(initialSaved.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(initialSaved.itemsPerPage || 10);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Pagination metadata
  const [paginationMeta, setPaginationMeta] = useState(() => {
    return memoryTabCache[initialTab]?.meta || {
      page: initialSaved.currentPage || 1,
      limit: initialSaved.itemsPerPage || 10,
      total: 0,
    };
  });

  const [dataLoadedCount, setDataLoadedCount] = useState(0);
  const containerRef = useRef(null);
  const restoredScrollRef = useRef(initialSaved.scrollY || 0);

  // Instant scroll restoration before browser paint
  useLayoutEffect(() => {
    if (initialSaved.scrollY > 0 && containerRef.current) {
      containerRef.current.scrollTop = initialSaved.scrollY;
    }
  }, []);

  // Filter Kategori (untuk tab Data Produk & Data Layanan)
  const [filterCategories, setFilterCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(initialSaved.categoryFilter || "");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterMenuRef = useRef(null);

  // Load daftar kategori sekali untuk dropdown filter
  useEffect(() => {
    let cancelled = false;
    masterService.categories
      .listAll()
      .then((res) => {
        if (!cancelled) setFilterCategories(Array.isArray(res) ? res : res?.data || []);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset filter saat tab berubah ke tab yang tidak support filter kategori
  useEffect(() => {
    if (activeTab !== "Data Produk" && activeTab !== "Data Layanan") {
      setCategoryFilter("");
      setIsFilterOpen(false);
    }
  }, [activeTab]);

  // Close filter popup saat klik di luar
  useEffect(() => {
    if (!isFilterOpen) return;
    const handleClickOutside = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterOpen]);

  // Persist state perubahan tab saat state berubah
  useEffect(() => {
    saveTabState(activeTab, {
      currentPage,
      itemsPerPage,
      searchTerm,
      categoryFilter,
    });
  }, [activeTab, currentPage, itemsPerPage, searchTerm, categoryFilter]);

  // Track scroll on container & window
  useEffect(() => {
    const el = containerRef.current;
    const handleScroll = () => {
      if (restoredScrollRef.current > 0 || loading) return;
      const y = (el && el.scrollTop > 0) ? el.scrollTop : (window.scrollY || document.documentElement.scrollTop || 0);
      saveTabState(activeTab, { scrollY: y });
    };

    if (el) {
      el.addEventListener("scroll", handleScroll, { passive: true });
    }
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (el) el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeTab, loading]);

  // Restore scroll position after data loads if not already restored
  useEffect(() => {
    if (dataLoadedCount > 0 && !loading && restoredScrollRef.current > 0) {
      const targetY = restoredScrollRef.current;
      restoredScrollRef.current = 0;
      if (containerRef.current) {
        containerRef.current.scrollTop = targetY;
      }
    }
  }, [dataLoadedCount, loading]);

  const filterAvailable =
    activeTab === "Data Produk" || activeTab === "Data Layanan";

  const [productData, setProductData] = useState(() => memoryTabCache["Data Produk"]?.data || []);
  const [serviceData, setServiceData] = useState(() => memoryTabCache["Data Layanan"]?.data || []);
  const [unitData, setUnitData] = useState(() => memoryTabCache["Data Unit"]?.data || []);
  const [brandData, setBrandData] = useState(() => memoryTabCache["Data Merek"]?.data || []);
  const [supplierData, setSupplierData] = useState(() => memoryTabCache["Data Supplier"]?.data || []);
  const [categoryData, setCategoryData] = useState(() => memoryTabCache["Data Kategori"]?.data || []);
  const [satuanData, setSatuanData] = useState(() => memoryTabCache["Data Satuan"]?.data || []);
  const [salesTypeData, setSalesTypeData] = useState(() => memoryTabCache["Tipe Penjualan"]?.data || []);

  // Mock Data for Products
  const mockProductData = [
    {
      id: 1,
      status: "Aktif",
      name: "Air Filter Radial 131-8822 X",
      category: "Filter",
      unit: "Unit",
      buyPrice: 372000,
      margin: 40,
      sellPrice: 620000,
    },
    {
      id: 2,
      status: "Aktif",
      name: "Busing Kuping Komatsu 80X110",
      category: "Busing",
      unit: "Pcs",
      buyPrice: "3 Varian Harga",
      margin: 40,
      sellPrice: "3 Varian Harga",
    },
    {
      id: 3,
      status: "Aktif",
      name: "Filter Element 207-60-71182",
      category: "Filter",
      unit: "Set",
      buyPrice: 172000,
      margin: 60,
      sellPrice: 430000,
    },
    {
      id: 4,
      status: "Non-Aktif",
      name: "Injector Assy Denso Kobelco",
      category: "Injector",
      unit: "Unit",
      buyPrice: 1560000,
      margin: 22,
      sellPrice: 2000000,
    },
    {
      id: 5,
      status: "Aktif",
      name: "Oli Filter HH164-32430 PC75",
      category: "Filter",
      unit: "Set",
      buyPrice: "2 Varian Harga",
      margin: 30,
      sellPrice: "2 Varian Harga",
    },
    {
      id: 6,
      status: "Aktif",
      name: "Sensor Pressure 4436535 Swing",
      category: "Sensor",
      unit: "Box",
      buyPrice: 1750000,
      margin: 23,
      sellPrice: 2275000,
    },
  ];

  // Mock Data for Services
  const mockServiceData = [
    {
      id: 1,
      status: "Aktif",
      name: "Press Hose 3/8 *40 cm",
      category: "Press Hose",
      unit: "Paket",
      buyPrice: 250000,
      composition: 3,
      sellPrice: 350000,
    },
    {
      id: 2,
      status: "Aktif",
      name: "Press Hose 3/8 *220 cm",
      category: "Press Hose",
      unit: "Paket",
      buyPrice: 725000,
      composition: 3,
      sellPrice: 950000,
    },
    {
      id: 3,
      status: "Aktif",
      name: "Press Hose 3/8 *130 cm",
      category: "Press Hose",
      unit: "Paket",
      buyPrice: 580000,
      composition: 4,
      sellPrice: 800000,
    },
    {
      id: 4,
      status: "Non-Aktif",
      name: "Hose Rod Bawah ZAK 210-5G",
      category: "Press Hose",
      unit: "Paket",
      buyPrice: 0,
      composition: 0,
      sellPrice: 300000,
    },
    {
      id: 5,
      status: "Aktif",
      name: "Selang Hose 3/2 *(65cm)",
      category: "Press Hose",
      unit: "Paket",
      buyPrice: 200000,
      composition: 2,
      sellPrice: 300000,
    },
  ];

  // Mock Data for Units
  const mockUnitData = [
    {
      id: 1,
      status: "Aktif",
      name: "KOMATSU 07",
      type: "Alat Berat",
      totalTx: "245 Nota",
      totalUsage: 18000000,
      lastTx: "18 Jan 2026, 09:00",
    },
    {
      id: 2,
      status: "Aktif",
      name: "KOMATSU 08",
      type: "Alat Berat",
      totalTx: "108 Nota",
      totalUsage: 12185000,
      lastTx: "24 Jan 2026, 11:46",
    },
    {
      id: 3,
      status: "Aktif",
      name: "HITACHI 10",
      type: "Alat Berat",
      totalTx: "123 Nota",
      totalUsage: 9000000,
      lastTx: "16 Jan 2026, 15:20",
    },
    {
      id: 4,
      status: "Aktif",
      name: "HITACHI 15",
      type: "Alat Berat",
      totalTx: "156 Nota",
      totalUsage: 47500000,
      lastTx: "14 Jan 2026, 15:30",
    },
    {
      id: 5,
      status: "Non-Aktif",
      name: "PICKUP L300",
      type: "Mobil",
      totalTx: "172 Nota",
      totalUsage: 10090000,
      lastTx: "24 Des 2025, 13:20",
    },
    {
      id: 6,
      status: "Aktif",
      name: "GRANDMAX",
      type: "Mobil",
      totalTx: "148 Nota",
      totalUsage: 13500000,
      lastTx: "20 Jan 2026, 10:05",
    },
  ];

  // Mock Data for Brands
  const mockBrandData = [
    { id: 1, status: "Aktif", name: "Komatsu", totalProducts: 12 },
    { id: 2, status: "Aktif", name: "Hitachi", totalProducts: 8 },
    { id: 3, status: "Aktif", name: "Kobelco", totalProducts: 5 },
    { id: 4, status: "Aktif", name: "Caterpillar", totalProducts: 15 },
    { id: 5, status: "Aktif", name: "Denso", totalProducts: 3 },
  ];

  // Mock Data for Suppliers
  const mockSupplierData = [
    {
      id: 1,
      status: "Aktif",
      name: "Global Mandiri Tractor",
      address: "Palembang, Sumatera Selatan",
      phone: "081234567890",
      totalProducts: 0,
    },
    {
      id: 2,
      status: "Aktif",
      name: "PT. Karya Niaga Sukses",
      address: "Karang Anyar, Jakarta Pusat",
      phone: "081234567890",
      totalProducts: 0,
    },
    {
      id: 3,
      status: "Non-Aktif",
      name: "Mekar Jaya Mandiri",
      address: "Jakarta Barat, DKI Jakarta",
      phone: "081234567890",
      totalProducts: 0,
    },
    {
      id: 4,
      status: "Aktif",
      name: "UD. Utama Diesel",
      address: "Palembang, Sumatera Selatan",
      phone: "081234567890",
      totalProducts: 0,
    },
  ];

  // Mock Data for Categories
  const mockCategoryData = [
    { id: 1, status: "Aktif", name: "Alat Berat", totalProducts: 4 },
    { id: 2, status: "Aktif", name: "Mobil", totalProducts: 2 },
    { id: 3, status: "Aktif", name: "Filter", totalProducts: 3 },
    { id: 4, status: "Aktif", name: "Busing", totalProducts: 1 },
    { id: 5, status: "Aktif", name: "Injector", totalProducts: 1 },
    { id: 6, status: "Aktif", name: "Sensor", totalProducts: 1 },
    { id: 7, status: "Aktif", name: "Press Hose", totalProducts: 5 },
  ];

  // Mock Data for Satuan
  const mockSatuanData = [
    { id: 1, status: "Aktif", name: "Unit", totalProducts: 0 },
    { id: 2, status: "Aktif", name: "Pcs", totalProducts: 0 },
    { id: 3, status: "Aktif", name: "Set", totalProducts: 0 },
    { id: 4, status: "Aktif", name: "Box", totalProducts: 0 },
    { id: 5, status: "Non-Aktif", name: "Drum", totalProducts: 0 },
    { id: 6, status: "Aktif", name: "Liter", totalProducts: 0 },
  ];

  // Mock Data for Sales Type
  const mockSalesTypeData = [
    {
      id: 1,
      status: "Aktif",
      name: "Internal",
      description: "Forem ipsum dolor sit amet, consectetur adipiscing elit.",
    },
    {
      id: 2,
      status: "Aktif",
      name: "Umum",
      description: "Forem ipsum dolor sit amet, consectetur adipiscing elit.",
    },
    {
      id: 3,
      status: "Non-Aktif",
      name: "Member",
      description: "Forem ipsum dolor sit amet, consectetur adipiscing elit.",
    },
  ];

  // Transform API data to match table expectations
  const transformProductData = (apiData) => {
    return (apiData || []).map((item) => {
      const hasVariants =
        (item.variants && Array.isArray(item.variants) && item.variants.length > 0) ||
        (item.options && Array.isArray(item.options) && item.options.length > 0);
      const variantCount = item.variants?.length || item.options?.length || 0;
      const firstVariant = item.variants?.[0] || null;
      const firstOption = item.options?.[0] || null;

      const buyPrice =
        (firstVariant ? firstVariant.purchase_price : 0) ||
        (firstOption ? firstOption.purchase_price : 0) ||
        item.purchase_price ||
        0;
      const sellPrice =
        (firstVariant ? firstVariant.sell_price : 0) ||
        (firstOption ? firstOption.price : 0) ||
        item.sell_price ||
        0;
      const margin =
        buyPrice > 0
          ? Math.round(((sellPrice - buyPrice) / buyPrice) * 100)
          : 0;

      return {
        ...item,
        status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
        category: item.category_name || "-",
        unit: item.unit_name || "-",
        buyPrice: buyPrice,
        sellPrice: sellPrice,
        margin: margin,
        hasVariants: hasVariants,
        variantCount: variantCount,
      };
    });
  };

  const transformBrandData = (apiData) => {
    return (apiData || []).map((item) => ({
      ...item,
      status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
      totalProducts: 0, // API doesn't provide this, placeholder for now
    }));
  };

  const transformCategoryData = (apiData) => {
    return (apiData || []).map((item) => ({
      ...item,
      status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
      totalProducts: 0,
    }));
  };

  const transformSupplierData = (apiData) => {
    return (apiData || []).map((item) => ({
      ...item,
      status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
      phone: item.phone_number,
      totalProducts: 0,
    }));
  };

  const transformUnitData = (apiData) => {
    return (apiData || []).map((item) => {
      const totalTransactions = Number(
        item.total_transactions ?? item.transaction_count ?? item.total_tx ?? 0,
      );
      const totalUsage = Number(
        item.total_usage ?? item.total_pemakaian ?? item.usage_total ?? 0,
      );
      const lastTransaction =
        item.last_transaction ?? item.latest_transaction ?? item.last_tx;
      const lastTransactionDate = lastTransaction
        ? new Date(lastTransaction)
        : null;
      const hasValidLastTransaction =
        lastTransactionDate && !Number.isNaN(lastTransactionDate.getTime());

      return {
        ...item,
        status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
        type: item.jenis || "N/A",
        notes: item.keterangan || item.descriptions || "",
        totalTx: `${Number.isFinite(totalTransactions) ? totalTransactions : 0} Nota`,
        totalUsage: Number.isFinite(totalUsage) ? totalUsage : 0,
        lastTx: hasValidLastTransaction
          ? formatDateTime(lastTransactionDate)
          : "-",
      };
    });
  };

  const transformSatuanData = (apiData) => {
    return (apiData || []).map((item) => ({
      ...item,
      status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
      totalProducts: 0,
    }));
  };

  const transformSalesTypeData = (apiData) => {
    return (apiData || []).map((item) => ({
      ...item,
      status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
    }));
  };

  const transformServiceData = (apiData) => {
    return (apiData || []).map((item) => ({
      ...item,
      category: item.category_name || "-",
      unit: item.unit_name || "-",
      sellPrice: item.sell_price || 0,
      buyPrice: item.total_buy_price || item.total_sell_price || 0,
      composition: item.count_of_products || item.compositions?.length || 0,
      status:
        item.status === "NON_ACTIVE" || item.status === "INACTIVE"
          ? "Non-Aktif"
          : "Aktif",
    }));
  };

  // Calculate pagination values
  const getTotalPages = () =>
    Math.ceil(paginationMeta.total / paginationMeta.limit) || 1;
  const getStartEntry = () =>
    paginationMeta.total === 0
      ? 0
      : (paginationMeta.page - 1) * paginationMeta.limit + 1;
  const getEndEntry = () =>
    Math.min(paginationMeta.page * paginationMeta.limit, paginationMeta.total);

  // Fetch data from API
  const fetchData = async () => {
    if (activeTab === "Kelola Addon") {
      setLoading(false);
      return;
    }
    const hasCachedData = (memoryTabCache[activeTab]?.data?.length || 0) > 0;
    if (!hasCachedData) {
      setLoading(true);
    }
    setError(null);

    try {
      // Fetch all products for stats client-side
      let productsList = [];
      if (
        activeTab === "Data Merek" ||
        activeTab === "Data Kategori" ||
        activeTab === "Data Satuan"
      ) {
        try {
          const prodRes = await masterService.products.list({ limit: 1000 });
          productsList = prodRes.data || [];
        } catch (err) {
          console.warn("Failed to fetch products for stats:", err);
        }
      }

      let poList = [];
      if (activeTab === "Data Supplier") {
        try {
          const poRes = await inventoryService.getPurchaseOrders({ limit: 1000 });
          poList = poRes.data || [];
        } catch (err) {
          console.warn("Failed to fetch PO for supplier stats:", err);
        }
      }

      let response;
      let finalData = [];
      if (activeTab === "Data Produk") {
        const params = {
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        };
        if (categoryFilter) params.category_id = categoryFilter;
        response = await masterService.products.list(params);
        finalData = transformProductData(response.data);
        setProductData(finalData);
      } else if (activeTab === "Data Layanan") {
        const params = {
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        };
        if (categoryFilter) params.category_id = categoryFilter;
        response = await masterService.services.list(params);
        finalData = transformServiceData(response.data);
        setServiceData(finalData);
      } else if (activeTab === "Data Unit") {
        response = await masterService.assetUnits.list({
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        });
        finalData = transformUnitData(response.data);
        setUnitData(finalData);
      } else if (activeTab === "Data Merek") {
        response = await masterService.brands.list({
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        });
        finalData = (response.data || []).map((item) => {
          const totalProducts = productsList.filter((p) =>
            p.variants?.some((v) => v.brand_id === item.id)
          ).length;
          return {
            ...item,
            status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
            totalProducts,
          };
        });
        setBrandData(finalData);
      } else if (activeTab === "Data Supplier") {
        response = await masterService.suppliers.list({
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        });
        finalData = (response.data || []).map((item) => {
          const supplierPOs = poList.filter(
            (po) => po.supplier_id === item.id || po.supplier?.id === item.id
          );
          const uniqueProductIds = new Set();
          supplierPOs.forEach((po) => {
            (po.items || []).forEach((poItem) => {
              if (poItem.product_id) {
                uniqueProductIds.add(poItem.product_id);
              }
            });
          });
          return {
            ...item,
            status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
            phone: item.phone_number,
            totalProducts: uniqueProductIds.size,
          };
        });
        setSupplierData(finalData);
      } else if (activeTab === "Data Kategori") {
        response = await masterService.categories.list({
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        });
        finalData = (response.data || []).map((item) => {
          const totalProducts = productsList.filter(
            (p) => p.category_id === item.id
          ).length;
          return {
            ...item,
            status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
            totalProducts,
          };
        });
        setCategoryData(finalData);
      } else if (activeTab === "Data Satuan") {
        response = await masterService.units.list({
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        });
        finalData = (response.data || []).map((item) => {
          const totalProducts = productsList.filter(
            (p) => p.unit_id === item.id || p.variants?.some((v) => v.unit_id === item.id)
          ).length;
          return {
            ...item,
            status: item.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
            totalProducts,
          };
        });
        setSatuanData(finalData);
      } else if (activeTab === "Tipe Penjualan") {
        response = await masterService.orderTypes.list({
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        });
        finalData = transformSalesTypeData(response.data);
        setSalesTypeData(finalData);
      } else if (activeTab === "Data Lokasi Stok") {
        response = await masterService.stockLocations.list({
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        });
        finalData = response.data || response || [];
        setStockLocationData(finalData);
      } else if (activeTab === "Data Meja") {
        response = await masterService.tables.list({
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        });
        finalData = response.data || [];
        setTableData(finalData);
      } else if (activeTab === "Label Barcode") {
        response = await barcodeLabelService.list({
          search: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        });
        finalData = response.data || [];
        setBarcodeLabelData(finalData);
      }

      memoryTabCache[activeTab] = {
        data: finalData,
        meta: response?.meta || { page: currentPage, limit: itemsPerPage, total: finalData.length },
      };

      // Update pagination metadata from API response
      if (response && response.meta) {
        setPaginationMeta({
          page: response.meta.page || currentPage,
          limit: response.meta.limit || itemsPerPage,
          total: response.meta.total || 0,
        });
      } else {
        // Fallback if no meta in response
        setPaginationMeta({
          page: currentPage,
          limit: itemsPerPage,
          total: finalData.length || 0,
        });
      }
    } catch (err) {
      const errMsg = err.message || "Gagal memuat data";
      setError(errMsg);
      showError(errMsg);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
      setDataLoadedCount((prev) => prev + 1);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, currentPage, itemsPerPage, refreshTrigger, categoryFilter]);

  const [isApps, setIsApps] = useState(() => localStorage.getItem("PREVIEW_APPS") === "true");

  useEffect(() => {
    const handleStorage = () => {
      setIsApps(localStorage.getItem("PREVIEW_APPS") === "true");
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener("tenantUpdated", handleStorage);
    window.addEventListener("modeUpdated", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("tenantUpdated", handleStorage);
      window.removeEventListener("modeUpdated", handleStorage);
    };
  }, []);

  // Sync activeTab with URL changes
  useEffect(() => {
    let newTab = getTabFromPath(location.pathname);
    if ((newTab === "Data Lokasi Stok" || newTab === "Label Barcode") && isApps) {
      navigate("/master-data/produk", { replace: true });
      newTab = "Data Produk";
    }
    if ((newTab === "Kelola Addon" || newTab === "Data Meja") && !isApps) {
      navigate("/master-data/produk", { replace: true });
      newTab = "Data Produk";
    }
    if (newTab !== activeTab) {
      // 1. Simpan state tab yang ditinggalkan (termasuk scroll)
      saveTabState(activeTab, {
        currentPage,
        itemsPerPage,
        searchTerm,
        categoryFilter,
        scrollY: window.scrollY || 0,
      });

      // 2. Muat state tab baru jika ada (default ke 10 jika belum pernah di-set)
      const nextSaved = getSavedTabState(newTab);
      setActiveTab(newTab);
      setCurrentPage(nextSaved?.currentPage || 1);
      setItemsPerPage(nextSaved?.itemsPerPage || 10);
      setSearchTerm(nextSaved?.searchTerm || "");
      setCategoryFilter(nextSaved?.categoryFilter || "");
      setPaginationMeta({
        page: nextSaved?.currentPage || 1,
        limit: nextSaved?.itemsPerPage || 10,
        total: 0,
      });
      restoredScrollRef.current = nextSaved?.scrollY || 0;
    }
  }, [location.pathname, navigate, activeTab, currentPage, itemsPerPage, searchTerm, categoryFilter, isApps]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchData();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handler for page change - scroll to top
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    saveTabState(activeTab, { currentPage: newPage, scrollY: 0 });
    restoredScrollRef.current = 0;
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handler for changing items per page - reset to page 1 & scroll to top
  const handleItemsPerPageChange = (newLimit) => {
    const numLimit = Number(newLimit) || 10;
    setItemsPerPage(numLimit);
    setCurrentPage(1);
    saveTabState(activeTab, { itemsPerPage: numLimit, currentPage: 1, scrollY: 0 });
    restoredScrollRef.current = 0;
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const allTabs = [
    { label: "Data Produk", icon: Package },
    { label: "Kelola Addon", icon: Boxes },
    { label: "Data Layanan", icon: Briefcase },
    { label: "Data Unit", icon: Truck },
    { label: "Data Merek", icon: Tag },
    { label: "Data Supplier", icon: Truck },
    { label: "Data Kategori", icon: FolderTree },
    { label: "Data Satuan", icon: Ruler },
    { label: "Tipe Penjualan", icon: ShoppingBag },
    { label: "Data Lokasi Stok", icon: MapPin },
    { label: "Data Meja", icon: Utensils },
  ];

  const tabs = isApps
    ? allTabs.filter((t) => t.label !== "Data Lokasi Stok")
    : allTabs.filter((t) => t.label !== "Kelola Addon" && t.label !== "Data Meja");

  const handleEditTable = (tableItem) => {
    setSelectedTable(tableItem);
    setIsAddTableModalOpen(true);
  };

  const handleDetailTable = (tableItem) => {
    setSelectedTableForDetail(tableItem);
    setIsTableDetailModalOpen(true);
  };

  const handleStatusChangeTable = async (tableId, newStatus) => {
    try {
      await masterService.tables.updateStatus(tableId, { status: newStatus });
      showSuccess("Status meja berhasil diperbarui");
      setIsTableDetailModalOpen(false);
      await fetchData();
    } catch (err) {
      console.error("Error updating table status:", err);
      showError(err.message || "Gagal memperbarui status meja");
    }
  };

  const handleSaveTable = async (payload) => {
    try {
      if (selectedTable) {
        await masterService.tables.update(selectedTable.id, payload);
        showSuccess("Data meja berhasil diperbarui");
      } else {
        await masterService.tables.create(payload);
        showSuccess("Data meja berhasil ditambahkan");
      }
      setIsAddTableModalOpen(false);
      setSelectedTable(null);
      await fetchData();
    } catch (err) {
      console.error("Error saving table:", err);
      showError(err.message || "Gagal menyimpan data meja");
    }
  };

  const handleDeleteTable = (tableItem) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Meja?",
      message: `Apakah Anda yakin ingin menghapus "${tableItem.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.tables.delete(tableItem.id);
          showSuccess("Data meja berhasil dihapus");
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
          await fetchData();
        } catch (err) {
          console.error("Error deleting table:", err);
          showError(err.message || "Gagal menghapus meja");
        }
      },
    });
  };

  const handleEditStockLocation = (item) => {
    setSelectedStockLocation(item);
    setIsAddStockLocationModalOpen(true);
  };

  const handleDetailStockLocation = (item) => {
    const el = containerRef.current;
    const y = (el && el.scrollTop > 0) ? el.scrollTop : (window.scrollY || document.documentElement.scrollTop || 0);

    saveTabState("Data Lokasi Stok", {
      searchTerm,
      currentPage,
      itemsPerPage,
      scrollY: y,
    });
    navigate(`/master-data/lokasi-stok/${item.id}`);
  };

  const handleDeleteStockLocation = (item) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Lokasi Stok?",
      message: `Apakah Anda yakin ingin menghapus lokasi stok "${item.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.stockLocations.delete(item.id);
          showSuccess("Lokasi stok berhasil dihapus");
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
          setRefreshTrigger((prev) => prev + 1);
        } catch (err) {
          console.error("Error deleting stock location:", err);
          showError(err.message || "Gagal menghapus lokasi stok");
        }
      },
    });
  };

  const handleInfoService = (service) => {
    navigate(`/inventori/layanan/${service.id}`);
  };

  const handleInfoUnit = (unit) => {
    navigate(`/inventori/unit/${unit.id}`);
  };

  const handleAdd = () => {
    if (activeTab === "Data Produk") {
      setSelectedProduct(null);
      setIsAddProductModalOpen(true);
    } else if (activeTab === "Data Layanan") {
      setSelectedService(null);
      setIsAddServiceModalOpen(true);
    } else if (activeTab === "Data Unit") {
      setSelectedUnit(null);
      setIsAddUnitModalOpen(true);
    } else if (activeTab === "Data Merek") {
      setSelectedBrand(null);
      setIsAddBrandModalOpen(true);
    } else if (activeTab === "Data Supplier") {
      setSelectedSupplier(null);
      setIsAddSupplierModalOpen(true);
    } else if (activeTab === "Data Kategori") {
      setSelectedCategory(null);
      setIsAddCategoryModalOpen(true);
    } else if (activeTab === "Data Satuan") {
      setSelectedSatuan(null);
      setIsAddSatuanModalOpen(true);
    } else if (activeTab === "Tipe Penjualan") {
      setSelectedSalesType(null);
      setIsAddSalesTypeModalOpen(true);
    } else if (activeTab === "Data Lokasi Stok") {
      setSelectedStockLocation(null);
      setIsAddStockLocationModalOpen(true);
    } else if (activeTab === "Data Meja") {
      setSelectedTable(null);
      setIsAddTableModalOpen(true);
    } else if (activeTab === "Label Barcode") {
      setSelectedBarcodeLabel(null);
      setIsCreateBarcodeModalOpen(true);
    }
  };

  const handlePrintBarcodeLabel = (item) => {
    setQuickPrintBarcodeLabel(item);
  };

  const handleDetailBarcodeLabel = (item) => {
    setSelectedBarcodeLabel(item);
    setIsCreateBarcodeModalOpen(true);
  };

  const handleDeleteBarcodeLabel = (item) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Label Barcode?",
      message: `Apakah Anda yakin ingin menghapus label barcode "${item.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await barcodeLabelService.delete(item.id);
          showSuccess("Label barcode berhasil dihapus");
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
          await fetchData();
        } catch (err) {
          console.error("Error deleting barcode label:", err);
          showError(err.message || "Gagal menghapus label barcode");
        }
      },
    });
  };

  const handleEditProduct = (product) => {
    // Simpan filter, limit, halaman aktif & scroll position sebelum ke detail
    const el = containerRef.current;
    const y = (el && el.scrollTop > 0) ? el.scrollTop : (window.scrollY || document.documentElement.scrollTop || 0);

    saveTabState("Data Produk", {
      searchTerm,
      categoryFilter,
      currentPage,
      itemsPerPage,
      scrollY: y,
    });
    navigate(`/master-data/produk/${product.id}`);
  };

  const handleDeleteProduct = (product) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Produk?",
      message: `Apakah anda yakin ingin menghapus produk "${product.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.products.delete(product.id);
          showSuccess("Produk berhasil dihapus");
          fetchData();
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        } catch (err) {
          console.error("Error deleting product:", err);
          showError(err.message || "Gagal menghapus produk");
        }
      },
    });
  };

  const handleEditService = (service) => {
    setSelectedService(service);
    setIsAddServiceModalOpen(true);
  };

  const handleSaveService = async (payload) => {
    try {
      if (selectedService) {
        // Update existing service
        await masterService.services.update(selectedService.id, payload);
        showSuccess("Layanan berhasil diperbarui");
      } else {
        // Create new service
        await masterService.services.create(payload);
        showSuccess("Layanan berhasil ditambahkan");
      }
      await fetchData();
      setSelectedService(null);
    } catch (err) {
      console.error("Error saving service:", err);
      showError(err.message || "Gagal menyimpan layanan");
      throw new Error(err.message || "Gagal menyimpan layanan");
    }
  };

  const handleDeleteService = (service) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Layanan?",
      message: `Apakah anda yakin ingin menghapus layanan "${service.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.services.delete(service.id);
          showSuccess("Layanan berhasil dihapus");
          await fetchData();
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        } catch (err) {
          console.error("Error deleting service:", err);
          showError(err.message || "Gagal menghapus layanan");
        }
      },
    });
  };

  const handleEditUnit = (unit) => {
    setSelectedUnit(unit);
    setIsAddUnitModalOpen(true);
  };

  const handleSaveUnit = async (payload, existingData) => {
    try {
      if (existingData) {
        // Update existing unit
        await masterService.assetUnits.update(existingData.id, payload);
        showSuccess("Unit berhasil diperbarui");
      } else {
        // Create new unit
        await masterService.assetUnits.create(payload);
        showSuccess("Unit berhasil ditambahkan");
      }
      await fetchData();
    } catch (err) {
      console.error("Error saving unit:", err);
      showError(err.message || "Gagal menyimpan unit");
      throw new Error(err.message || "Gagal menyimpan unit");
    }
  };

  const handleDeleteUnit = (unit) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Unit?",
      message: `Apakah anda yakin ingin menghapus unit "${unit.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.assetUnits.delete(unit.id);
          showSuccess("Unit berhasil dihapus");
          fetchData();
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        } catch (err) {
          console.error("Error deleting unit:", err);
          showError(err.message || "Gagal menghapus unit");
        }
      },
    });
  };

  const handleEditBrand = (brand) => {
    setSelectedBrand(brand);
    setIsAddBrandModalOpen(true);
  };

  const handleDeleteBrand = (brand) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Merek?",
      message: `Apakah anda yakin ingin menghapus merek "${brand.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.brands.delete(brand.id);
          showSuccess("Merek berhasil dihapus");
          fetchData();
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        } catch (err) {
          console.error("Error deleting brand:", err);
          showError(err.message || "Gagal menghapus merek");
        }
      },
    });
  };

  const handleEditSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setIsAddSupplierModalOpen(true);
  };

  const handleDeleteSupplier = (supplier) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Supplier?",
      message: `Apakah anda yakin ingin menghapus supplier "${supplier.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.suppliers.delete(supplier.id);
          showSuccess("Supplier berhasil dihapus");
          fetchData();
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        } catch (err) {
          console.error("Error deleting supplier:", err);
          showError(err.message || "Gagal menghapus supplier");
        }
      },
    });
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setIsAddCategoryModalOpen(true);
  };

  const handleDeleteCategory = (category) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Kategori?",
      message: `Apakah anda yakin ingin menghapus kategori "${category.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.categories.delete(category.id);
          showSuccess("Kategori berhasil dihapus");
          fetchData();
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        } catch (err) {
          console.error("Error deleting category:", err);
          showError(err.message || "Gagal menghapus kategori");
        }
      },
    });
  };

  const handleEditSatuan = (satuan) => {
    setSelectedSatuan(satuan);
    setIsAddSatuanModalOpen(true);
  };

  const handleDeleteSatuan = (satuan) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Satuan?",
      message: `Apakah anda yakin ingin menghapus satuan "${satuan.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.units.delete(satuan.id);
          showSuccess("Satuan berhasil dihapus");
          fetchData();
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        } catch (err) {
          console.error("Error deleting satuan:", err);
          showError(err.message || "Gagal menghapus satuan");
        }
      },
    });
  };

  const handleEditSalesType = (salesType) => {
    setSelectedSalesType(salesType);
    setIsAddSalesTypeModalOpen(true);
  };

  const handleDeleteSalesType = (salesType) => {
    setConfirmationModal({
      isOpen: true,
      title: "Hapus Tipe Penjualan?",
      message: `Apakah anda yakin ingin menghapus tipe penjualan "${salesType.name}"?`,
      type: "danger",
      confirmText: "Hapus",
      onConfirm: async () => {
        try {
          await masterService.orderTypes.delete(salesType.id);
          showSuccess("Tipe penjualan berhasil dihapus");
          fetchData();
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        } catch (err) {
          console.error("Error deleting sales type:", err);
          showError(err.message || "Gagal menghapus tipe penjualan");
        }
      },
    });
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() =>
          setIsSidebarCollapsed((prev) => {
            const newState = !prev;
            localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
            return newState;
          })
        }
        activePage="master-data"
      />

      <div
        ref={containerRef}
        className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header />

        <div className="p-6">
          {/* Top Card: Page Title & Actions */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-5 mb-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Left: Page Title */}
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                  <Database className="w-6 h-6 text-teal-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900">
                    Master Data
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Kelola data master toko Anda
                  </p>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2.5 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari data . . ."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                      saveTabState(activeTab, { searchTerm: e.target.value, currentPage: 1, scrollY: 0 });
                    }}
                    className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 focus:bg-white transition-colors"
                  />
                </div>

                {filterAvailable && (
                  <div className="relative" ref={filterMenuRef}>
                    <button
                      type="button"
                      onClick={() => setIsFilterOpen((prev) => !prev)}
                      className={`p-2 ring-1 rounded-lg transition-colors relative ${categoryFilter
                          ? "bg-teal-50 ring-teal-300 text-teal-700"
                          : "bg-white ring-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                        }`}
                      title="Filter">
                      <Filter className="w-4 h-4" />
                      {categoryFilter && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-teal-500 rounded-full ring-2 ring-white" />
                      )}
                    </button>

                    {isFilterOpen && (
                      <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg ring-1 ring-slate-200/70 z-30 overflow-visible">
                        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between rounded-t-xl bg-white">
                          <p className="text-sm font-semibold text-slate-800">
                            Filter {activeTab}
                          </p>
                          <button
                            onClick={() => setIsFilterOpen(false)}
                            className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                            aria-label="Tutup filter">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="p-4 space-y-3">
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                              <Tag className="w-3 h-3" /> Filter Kategori
                            </p>
                            <SearchableSelect
                              options={[{ id: "", name: "Semua Kategori" }, ...filterCategories]}
                              value={categoryFilter}
                              onChange={(val) => {
                                setCategoryFilter(val || "");
                                setCurrentPage(1);
                              }}
                              placeholder="Semua Kategori"
                              displayKey="name"
                              valueKey="id"
                              searchable={true}
                              size="sm"
                            />
                          </div>
                        </div>
                        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between rounded-b-xl">
                          <button
                            onClick={() => {
                              setCategoryFilter("");
                              setCurrentPage(1);
                            }}
                            disabled={!categoryFilter}
                            className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors">
                            Reset
                          </button>
                          <button
                            onClick={() => setIsFilterOpen(false)}
                            className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700 transition-colors">
                            Terapkan
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab !== "Kelola Addon" && (
                  <button
                    onClick={handleAdd}
                    className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors whitespace-nowrap shadow-sm shadow-teal-600/20">
                    <Plus className="w-4 h-4" />
                    <span>{activeTab === "Label Barcode" ? "Buat Label Barcode" : "Tambah"}</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 p-3 mb-6 shadow-sm">
            <div className="overflow-x-auto -mx-1 px-1">
              <div className="flex items-center min-w-max gap-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon || Database;
                  const isActive = activeTab === tab.label;
                  return (
                    <button
                      key={tab.label}
                      onClick={() => {
                        const path = getPathFromTab(tab.label);
                        navigate(path);
                      }}
                      className={`group relative inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${isActive
                          ? "bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-md shadow-teal-600/30 ring-1 ring-teal-500"
                          : "bg-slate-50 text-slate-600 ring-1 ring-transparent hover:text-teal-700 hover:bg-teal-50 hover:ring-teal-200"
                        }`}>
                      <span
                        className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${isActive
                            ? "bg-white/20"
                            : "bg-white text-teal-600 group-hover:bg-teal-100"
                          }`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      {tab.label}
                      {tab.count && (
                        <span
                          className={`text-xs font-bold ${isActive ? "text-amber-200" : "text-slate-400"
                            }`}>
                          ({tab.count})
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Table Content */}
          {activeTab === "Data Produk" ? (
            <DataProductTable
              data={productData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={paginationMeta.total}
              startEntry={getStartEntry()}
              endEntry={getEndEntry()}
              maxPages={getTotalPages()}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
              onDetail={handleEditProduct}
            />
          ) : activeTab === "Kelola Addon" ? (
            <DataAddonTable searchTerm={searchTerm} />
          ) : activeTab === "Data Layanan" ? (
            <DataServiceTable
              data={serviceData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={paginationMeta.total}
              startEntry={getStartEntry()}
              endEntry={getEndEntry()}
              maxPages={getTotalPages()}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onEdit={handleEditService}
              onDelete={handleDeleteService}
              onInfo={handleInfoService}
            />
          ) : activeTab === "Data Unit" ? (
            <DataUnitTable
              data={unitData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={paginationMeta.total}
              startEntry={getStartEntry()}
              endEntry={getEndEntry()}
              maxPages={getTotalPages()}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onEdit={handleEditUnit}
              onDelete={handleDeleteUnit}
              onInfo={handleInfoUnit}
            />
          ) : activeTab === "Data Merek" ? (
            <DataBrandTable
              data={brandData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={paginationMeta.total}
              startEntry={getStartEntry()}
              endEntry={getEndEntry()}
              maxPages={getTotalPages()}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onEdit={handleEditBrand}
              onDelete={handleDeleteBrand}
            />
          ) : activeTab === "Data Supplier" ? (
            <DataSupplierTable
              data={supplierData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={paginationMeta.total}
              startEntry={getStartEntry()}
              endEntry={getEndEntry()}
              maxPages={getTotalPages()}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onEdit={handleEditSupplier}
              onDelete={handleDeleteSupplier}
            />
          ) : activeTab === "Data Kategori" ? (
            <DataCategoryTable
              data={categoryData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={paginationMeta.total}
              startEntry={getStartEntry()}
              endEntry={getEndEntry()}
              maxPages={getTotalPages()}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onEdit={handleEditCategory}
              onDelete={handleDeleteCategory}
            />
          ) : activeTab === "Data Satuan" ? (
            <DataSatuanTable
              data={satuanData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={paginationMeta.total}
              startEntry={getStartEntry()}
              endEntry={getEndEntry()}
              maxPages={getTotalPages()}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onEdit={handleEditSatuan}
              onDelete={handleDeleteSatuan}
            />
          ) : activeTab === "Tipe Penjualan" ? (
            <DataSalesTypeTable
              data={salesTypeData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={paginationMeta.total}
              startEntry={getStartEntry()}
              endEntry={getEndEntry()}
              maxPages={getTotalPages()}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onEdit={handleEditSalesType}
              onDelete={handleDeleteSalesType}
            />
          ) : activeTab === "Data Lokasi Stok" ? (
            <DataStockLocationTable
              data={stockLocationData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={paginationMeta.total || stockLocationData.length}
              startEntry={getStartEntry() || (stockLocationData.length === 0 ? 0 : 1)}
              endEntry={getEndEntry() || stockLocationData.length}
              maxPages={Math.max(getTotalPages(), Math.ceil(stockLocationData.length / itemsPerPage) || 1)}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onEdit={handleEditStockLocation}
              onDelete={handleDeleteStockLocation}
              onDetail={handleDetailStockLocation}
              onRefresh={fetchData}
            />
          ) : activeTab === "Data Meja" ? (
            <DataTableTable
              tables={tableData}
              loading={loading}
              error={error}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalEntries={paginationMeta.total || tableData.length}
              startEntry={getStartEntry() || (tableData.length === 0 ? 0 : 1)}
              endEntry={getEndEntry() || tableData.length}
              maxPages={Math.max(getTotalPages(), Math.ceil(tableData.length / itemsPerPage) || 1)}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              onEdit={handleEditTable}
              onDelete={handleDeleteTable}
              onDetail={handleDetailTable}
            />
          ) : activeTab === "Label Barcode" ? (
            <DataBarcodeLabelTable
              data={barcodeLabelData}
              loading={loading}
              onPrint={handlePrintBarcodeLabel}
              onDetail={handleDetailBarcodeLabel}
              onDelete={handleDeleteBarcodeLabel}
              currentPage={currentPage}
              totalPages={getTotalPages()}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              totalItems={paginationMeta.total || barcodeLabelData.length}
            />
          ) : (
            <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-gray-200">
              Content for {activeTab} is under construction.
            </div>
          )}
        </div>
      </div>

      <CreateBarcodeLabelModal
        isOpen={isCreateBarcodeModalOpen}
        onClose={() => {
          setIsCreateBarcodeModalOpen(false);
          setSelectedBarcodeLabel(null);
        }}
        editData={selectedBarcodeLabel}
        onSuccess={fetchData}
      />

      <BarcodeLabelQuickPrint
        record={quickPrintBarcodeLabel}
        onDone={() => setQuickPrintBarcodeLabel(null)}
      />

      <AddStockLocationModal
        isOpen={isAddStockLocationModalOpen}
        onClose={() => {
          setIsAddStockLocationModalOpen(false);
          setSelectedStockLocation(null);
        }}
        editData={selectedStockLocation}
        onSuccess={() => setRefreshTrigger((prev) => prev + 1)}
      />

      <StockLocationDetailModal
        isOpen={isStockLocationDetailModalOpen}
        onClose={() => {
          setIsStockLocationDetailModalOpen(false);
          setSelectedStockLocationForDetail(null);
        }}
        data={selectedStockLocationForDetail}
      />

      <AddProductModal
        isOpen={isAddProductModalOpen}
        onClose={() => {
          setIsAddProductModalOpen(false);
          setSelectedProduct(null);
        }}
        initialData={selectedProduct}
        onSuccess={fetchData}
      />

      <AddServiceModal
        isOpen={isAddServiceModalOpen}
        onClose={() => {
          setIsAddServiceModalOpen(false);
          setSelectedService(null);
        }}
        initialData={selectedService}
        onSave={handleSaveService}
      />

      <AddUnitModal
        isOpen={isAddUnitModalOpen}
        onClose={() => {
          setIsAddUnitModalOpen(false);
          setSelectedUnit(null);
        }}
        initialData={selectedUnit}
        onSave={handleSaveUnit}
      />

      <AddBrandModal
        isOpen={isAddBrandModalOpen}
        onClose={() => setIsAddBrandModalOpen(false)}
        initialData={selectedBrand}
        onSuccess={fetchData}
      />

      <AddSupplierModal
        isOpen={isAddSupplierModalOpen}
        onClose={() => setIsAddSupplierModalOpen(false)}
        initialData={selectedSupplier}
        onSuccess={fetchData}
      />

      <AddCategoryModal
        isOpen={isAddCategoryModalOpen}
        onClose={() => setIsAddCategoryModalOpen(false)}
        initialData={selectedCategory}
        onSuccess={fetchData}
      />

      <AddSatuanModal
        isOpen={isAddSatuanModalOpen}
        onClose={() => setIsAddSatuanModalOpen(false)}
        initialData={selectedSatuan}
        onSuccess={fetchData}
      />

      <AddSalesTypeModal
        isOpen={isAddSalesTypeModalOpen}
        onClose={() => setIsAddSalesTypeModalOpen(false)}
        initialData={selectedSalesType}
        onSuccess={fetchData}
      />

      <AddTableModal
        isOpen={isAddTableModalOpen}
        onClose={() => {
          setIsAddTableModalOpen(false);
          setSelectedTable(null);
        }}
        onSubmit={handleSaveTable}
        initialData={selectedTable}
      />

      <TableDetailModal
        isOpen={isTableDetailModalOpen}
        onClose={() => {
          setIsTableDetailModalOpen(false);
          setSelectedTableForDetail(null);
        }}
        table={selectedTableForDetail}
        onEdit={handleEditTable}
        onDelete={handleDeleteTable}
        onStatusChange={handleStatusChangeTable}
      />

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() =>
          setConfirmationModal({ ...confirmationModal, isOpen: false })
        }
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        confirmText={confirmationModal.confirmText}
      />
    </div>
  );
}
